import React from 'react';

import { useId } from '@coursera/cds-common';

import type { InputLabelProps } from '@core/forms/FormLabel';
import { FormLabel } from '@core/forms/FormLabel';
import type { SelectProps, SelectOptionProps } from '@core/forms/Select';
import { Select, SelectOption } from '@core/forms/Select';
import { ariaLabelledByForMultipleLabels } from '@core/utils/a11y';

import getSilentSelectFieldCss, { classes } from './getSilentSelectFieldCss';
import SelectedValue from './SelectedValue';

export type SilentSelectFieldProps = {
  /**
   * To hide the label, use `labelPlacement`.
   */
  label: string;

  /**
   * Defines label placement. If `none`, label is hidden.
   * @default integrated
   */
  labelPlacement?: 'integrated' | 'stacked' | 'none';

  /**
   * Defines placeholder
   */
  placeholder?: string;
} & InputLabelProps &
  Pick<
    SelectProps,
    | 'onChange'
    | 'onClose'
    | 'onOpen'
    | 'className'
    | 'id'
    | 'inputProps'
    | 'name'
    | 'defaultValue'
    | 'value'
    | 'open'
    | 'children'
    | 'autoFocus'
  >;

/**
 * Memoize SelectedValue to avoid any additional re-renders
 */
const MemoizedSelectedValue = React.memo(SelectedValue);

/**
 * SilentSelectField takes the place of SelectField when padding or screen real estate is limited.
 * Use primarily for sorting or filtering.
 *
 * See [Props](__storybookUrl__/components-inputs-silentselectfield--default#props)
 */
const SilentSelectField = React.forwardRef(function SilentSelectField(
  props: SilentSelectFieldProps,
  ref: React.Ref<HTMLButtonElement>
): React.ReactElement {
  const {
    label,
    labelPlacement = 'integrated',
    placeholder,
    children,
    id,
    renderLabel,
    inputProps,
    className,
    ...selectProps
  } = props;

  const controlId = useId(id);
  const placeholderId = `${controlId}-value`;
  const labelId = `${controlId}-label`;

  const hideLabel = labelPlacement === 'none';
  const isStackedLabel = labelPlacement === 'stacked';

  const optionsMap = React.useMemo(() => {
    const childrenArr = React.Children.toArray(children);
    return childrenArr.reduce<Record<React.ReactText, React.ReactNode>>(
      (acc, option) => {
        if (!React.isValidElement(option)) return acc;

        const { value, children }: SelectOptionProps = option.props;

        acc[value] = children;

        return acc;
      },
      {}
    );
  }, [children]);

  const placeholderComponent = (
    <SelectOption key="placeholder" disabled hidden value="">
      <span>{placeholder}</span>
    </SelectOption>
  );

  const renderValue = (value: unknown) => {
    return (
      <MemoizedSelectedValue
        hideLabel={hideLabel || isStackedLabel}
        label={label}
        labelId={labelId}
        placeholder={placeholder}
        placeholderId={placeholderId}
        value={optionsMap[value as string]}
      />
    );
  };

  return (
    <div
      className={className}
      css={getSilentSelectFieldCss}
      data-testid="silent-select-field"
    >
      {!hideLabel && isStackedLabel && (
        <FormLabel
          aria-hidden
          className={classes.stackedLabel}
          component="div"
          data-testid="silent-select-form-label"
          renderLabel={renderLabel}
        >
          {label}
        </FormLabel>
      )}

      <Select
        ref={ref}
        displayEmpty
        className={classes.input}
        id={controlId}
        label={label}
        renderValue={renderValue}
        {...selectProps}
        SelectDisplayProps={{
          'aria-labelledby':
            inputProps?.['aria-labelledby'] ??
            ariaLabelledByForMultipleLabels(labelId, placeholderId),
        }}
      >
        {Array.isArray(children) && placeholder
          ? [placeholderComponent, ...children]
          : children}
      </Select>
    </div>
  );
});

export default SilentSelectField;
